import { call, debounce, select, takeLatest } from 'redux-saga/effects'
import { PcrBackupConstants } from '../store/pcrBackup.constants'
import { HomeConstants } from '../../home/store/home.constants'
import { getServiceIdSelector } from '../../user/selectors'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { getPcrIdSelector } from '../../home/selectors'
import { getHomeSelector } from '../../home/selectors/getHome.selector'
import { getPcrBackupConfigSelector, getCurrentPcrBackupMetadata } from '../store/pcrBackup.selectors'
import { IPcrBackupConfig, IPcrBackupMetadata } from '../store/pcrBackup.types'
import { IHomeState } from '../../home/store/home.types'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { IPcr } from '../../pcr/store/pcr.types'
import { isOnline } from '../../../utilities/isOnline.util'
import { triggerBackup } from './triggerBackup.saga'

function* readPcrAndTriggerBackup() {
  const authJwt: string = yield select(getAuthenticationJwtSelector)
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrId: string | undefined = yield select(getPcrIdSelector)
  if (!authJwt || !serviceId || !pcrId || !isOnline()) return

  const homeState: IHomeState = yield select(getHomeSelector)
  if (homeState.isPCROpening) return

  const pcrDocsService = new PcrDocsService(serviceId)
  const pcr: IPcr | null = yield call([pcrDocsService, pcrDocsService.getPCR], pcrId)
  if (pcr) {
    yield call(triggerBackup, pcr)
  }
}

function* handlePcrSaved(action: { type: string; pcrId: string }) {
  const authJwt: string = yield select(getAuthenticationJwtSelector)
  const serviceId: string = yield select(getServiceIdSelector)
  if (!authJwt || !serviceId || !isOnline()) return

  const homeState: IHomeState = yield select(getHomeSelector)
  if (homeState.isPCROpening) return

  const currentPcrId: string | undefined = yield select(getPcrIdSelector)
  if (!currentPcrId || currentPcrId !== action.pcrId) return

  const metadata: IPcrBackupMetadata = yield select(getCurrentPcrBackupMetadata)
  const config: IPcrBackupConfig = yield select(getPcrBackupConfigSelector)
  if (config.disabled) return
  const intervalMs = config.minIntervalMinutes * 60 * 1000
  const intervalElapsed =
    !metadata.lastSuccessfulBackupTime || Date.now() - metadata.lastSuccessfulBackupTime >= intervalMs

  if (!intervalElapsed) return

  const pcrDocsService = new PcrDocsService(serviceId)
  const pcr: IPcr | null = yield call([pcrDocsService, pcrDocsService.getPCR], action.pcrId)
  if (pcr) {
    yield call(triggerBackup, pcr)
  }
}

const BACKUP_DEBOUNCE_MS = 3000

export function* pcrBackupOnSaveSaga() {
  yield debounce(BACKUP_DEBOUNCE_MS, [PcrBackupConstants.PCR_SAVED], handlePcrSaved)
}

export function* pcrBackupOnCloseSaga() {
  yield takeLatest(
    (a: { type: string; isPCRClosing?: boolean }) =>
      a.type === HomeConstants.SET_IS_PCR_CLOSING && a.isPCRClosing === true,
    readPcrAndTriggerBackup
  )
}
